import { Popover } from '@headlessui/react';
import { IconChevronRight, IconLayoutGrid } from '@tabler/icons-react';
import { memo, useMemo, useCallback } from 'react';
import SceneOptionsLayoutSubMenu from './SceneOptionsMenuLayoutSubMenu';
import { SCENE_OPTION_MENU_ITEM_COMMON_CLASSNAME } from '../SceneOptionsMenuConstants';
import { updateClipData } from '../../../../clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../../../../clipUpdateDataUtils/constants';
import { usePopoverPosition } from '@/hooks/usePopoverPosition';
import { LayoutType, FitType } from '@/player-config/src/types/asset-types';
import { Clip } from '@/player-config/src/types/clip-types';
import { Scene, Scenes } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';

function SceneOptionsMenuLayout({
  sceneId,
  scenes,
  clip,
  compositeSegmentId
}: {
  sceneId: string;
  scenes: Scenes;
  clip: Clip;
  compositeSegmentId: string;
}) {
  const { popoverRef, position } = usePopoverPosition();
  const scene = useMemo(() => scenes.find(scene => scene.id === sceneId) as Scene, [scenes, sceneId]);

  const handleLayoutChange = (details: { layout: LayoutType; fitMode: FitType; updateAllScenes?: boolean }) => {
    if (details.updateAllScenes) {
      updateClipData({
        actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
        clipId: clip.id,
        layout: details.layout
      });
    } else {
      updateClipData({
        actionType: CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SCENE,
        clipId: clip.id,
        sceneId: scene.id,
        layout: details.layout,
        fitMode: details.fitMode,
        compositeId: compositeSegmentId
      });
    }
  };

  const handleMouseEnter = useCallback(() => {
    if (popoverRef.current) {
      const button = popoverRef.current.querySelector('button');
      if (button) {
        button.click();
      }
    }
  }, [popoverRef]);

  return (
    <Popover className="relative w-full" ref={popoverRef}>
      <Popover.Button className={`${SCENE_OPTION_MENU_ITEM_COMMON_CLASSNAME}`} onMouseEnter={handleMouseEnter}>
        <div className="flex items-center gap-2">
          <IconLayoutGrid className="size-4" />
          <div>Layout</div>
        </div>
        <IconChevronRight className="size-4" />
      </Popover.Button>
      <Popover.Panel
        className="absolute z-[100] w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
        style={{
          [position === 'right' ? 'left' : 'right']: 'calc(100% + 4px)',
          bottom: '0'
        }}
      >
        <SceneOptionsLayoutSubMenu onSelect={handleLayoutChange} scene={scene} />
      </Popover.Panel>
    </Popover>
  );
}

export default memo(SceneOptionsMenuLayout);
